import { For, Show, createMemo, createSignal, onCleanup, onMount, type JSX } from 'solid-js';
import { Badge } from '~/components/primitives/badge';
import { Btn } from '~/components/primitives/btn';
import { Glyph } from '~/components/glyph';
import { deck } from '~/lib/deck';
import { econRowsToCsv, downloadCsv, eventsToCsv, fixesToCsv, heatRowsToCsv } from '~/lib/csv';
import { ECON_LOG_CAP, econLog, summarizeEcon } from '~/lib/econ-log';
import { economy } from '~/lib/economy';
import { player } from '~/lib/player';
import { EVENT_BUFFER_CAP, clearEvents, eventsAccessor } from '~/lib/event-log';
import { heat } from '~/lib/heat';
import { HEAT_LOG_CAP, heatLog } from '~/lib/heat-log';
import {
  ACCURACY_SETTLED_THRESHOLD_M,
  FIX_BUFFER_CAP,
  geolocation,
  type TrackingState,
} from '~/lib/geolocation';
import { ghostWalk } from '~/lib/ghost-walk';
import { createNetworkInfo } from '~/lib/network-info';
import { isIos, isStandalone, meetsWakeLockMinIos, parseIosVersion } from '~/lib/platform';
import { createServiceWorkerInfo } from '~/lib/service-worker-info';
import { createStorageInfo } from '~/lib/storage-info';
import { formatDuration, formatMb, truncateMiddle } from '~/lib/format';
import { visibility } from '~/lib/visibility';
import { wakeLock, type WakeLockStatus } from '~/lib/wake-lock';

interface DiagDef {
  l: string;
  v: JSX.Element;
  tone?: string;
}

function DiagSection(props: { title: string; sub?: string; children: JSX.Element }) {
  return (
    <div style={{ padding: '12px 12px 8px', 'border-bottom': '1px solid var(--line-faint)' }}>
      <div
        style={{
          display: 'flex',
          'align-items': 'baseline',
          gap: '8px',
          'margin-bottom': '8px',
        }}
      >
        <div class="t-label">{props.title}</div>
        <Show when={props.sub}>
          <span class="t-micro">/ {props.sub}</span>
        </Show>
      </div>
      {props.children}
    </div>
  );
}

function DiagGrid(props: { items: DiagDef[] }) {
  return (
    <div
      style={{
        display: 'grid',
        'grid-template-columns': '1fr 1fr',
        gap: '4px',
        'font-family': 'var(--mono)',
        'font-size': '10px',
      }}
    >
      <For each={props.items}>
        {(d) => (
          <div
            style={{
              background: 'var(--sf-input)',
              border: '1px solid var(--line)',
              padding: '5px 7px',
            }}
          >
            <div class="t-micro" style={{ 'margin-bottom': '1px' }}>
              {d.l}
            </div>
            <div
              style={{
                'font-family': 'var(--mono)',
                'font-size': '10.5px',
                color: `var(--${d.tone ?? 'fg'})`,
                'letter-spacing': '0.02em',
                'word-break': 'break-all',
              }}
            >
              {d.v}
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

function wakeLockBadgeTone(status: WakeLockStatus): 'action' | 'warn' | 'hostile' | 'data' {
  if (status === 'held') return 'action';
  if (status === 'released') return 'warn';
  if (status === 'denied' || status === 'error') return 'hostile';
  return 'data';
}

function trackingBadgeTone(state: TrackingState): 'action' | 'warn' | 'hostile' | 'data' {
  if (state === 'tracking') return 'action';
  if (state === 'acquiring') return 'warn';
  if (state === 'error') return 'hostile';
  return 'data';
}

export function Diag() {
  const isDev = import.meta.env.DEV;
  const geo = geolocation;
  const sw = createServiceWorkerInfo();
  const storage = createStorageInfo();
  const network = createNetworkInfo();
  // DIAG is a thin control surface over the app-wide ghost-walk singleton; it
  // keeps running across tab switches. Dev-only by convention (controls gated).
  const ghost = isDev ? ghostWalk : null;

  const [now, setNow] = createSignal(Date.now());
  onMount(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => clearInterval(id));
  });

  const pageLoad = performance.timeOrigin;
  const uptimeMs = createMemo(() => now() - pageLoad);
  const heldDuration = createMemo(() => {
    const since = wakeLock.heldSince();
    return wakeLock.totalHeldMs() + (since ? now() - since : 0);
  });
  const trackingDuration = createMemo(() => {
    const start = geo.startedAt();
    return start && geo.state() === 'tracking' ? now() - start : 0;
  });
  const timeSinceLastFix = createMemo(() => {
    const fix = geo.latest();
    return fix ? now() - fix.timestamp : null;
  });

  const iosVersion = parseIosVersion();
  const startEverything = async () => {
    await wakeLock.acquire('diag');
    await geo.start('diag');
  };
  const stopEverything = () => {
    geo.stop();
    void wakeLock.release('diag');
    ghost?.stop();
  };

  const isoStamp = () => new Date().toISOString().replace(/[:.]/g, '-');

  const onDownloadFixes = () => {
    downloadCsv(`grid-os-fixes-${isoStamp()}.csv`, fixesToCsv(geo.fixes()));
  };

  const onDownloadEvents = () => {
    downloadCsv(`grid-os-events-${isoStamp()}.csv`, eventsToCsv(eventsAccessor()));
  };

  const onDownloadHeat = () => {
    downloadCsv(`grid-os-heat-${isoStamp()}.csv`, heatRowsToCsv(heatLog.rows()));
  };

  const onDownloadEcon = () => {
    downloadCsv(`grid-os-economy-${isoStamp()}.csv`, econRowsToCsv(econLog.rows()));
  };

  // Live economy readout — derived read-only from the log buffer + stores; it
  // adds no cost to the economic actions themselves.
  const econSummary = createMemo(() => summarizeEcon(econLog.rows()));
  const econItems = (): DiagDef[] => {
    const s = econSummary();
    return [
      { l: 'BALANCE', v: `¤${player.credits().toLocaleString()}`, tone: 'action' },
      { l: 'OWNED TOOLS', v: String(deck.ownedTools().length), tone: 'data' },
      { l: 'EARNED', v: `¤${s.earned.toLocaleString()}`, tone: 'action' },
      { l: 'SPENT', v: `¤${s.spent.toLocaleString()}`, tone: 'warn' },
      {
        l: 'NET',
        v: `${s.net < 0 ? '−' : ''}¤${Math.abs(s.net).toLocaleString()}`,
        tone: s.net >= 0 ? 'data' : 'hostile',
      },
      {
        l: 'LOG ROWS',
        v: `${econLog.count()} / ${ECON_LOG_CAP}`,
        tone: econLog.count() > 0 ? 'data' : 'fg-mute',
      },
    ];
  };
  // Median ¤ payout by tier (T1–T4), computed from the earn rows in the buffer.
  const tierRows = () =>
    [1, 2, 3, 4].map((t) => ({ t, ...(econSummary().byTier[t] ?? { count: 0, median: 0 }) }));
  // Active dual-node states: a raised (decaying) price multiplier or a live lockout.
  const activeDuals = () => {
    const t = now();
    return Object.keys(economy.states())
      .map((id) => ({
        id,
        mult: economy.priceMultiplier(id, t),
        lockSecs: Math.ceil(economy.lockoutRemainingMs(id, t) / 1000),
      }))
      .filter((d) => d.mult > 1.001 || d.lockSecs > 0);
  };

  const heatItems = (): DiagDef[] => [
    {
      l: 'HEAT',
      v: String(Math.round(heat.value())),
      tone: heat.value() > 50 ? 'hostile' : heat.value() > 25 ? 'warn' : 'data',
    },
    { l: 'RANGE', v: heat.label(), tone: heat.value() > 50 ? 'hostile' : 'data' },
    { l: 'ACTIVE EVENTS', v: String(heat.events().length), tone: 'data' },
    {
      l: 'LOG ROWS',
      v: `${heatLog.count()} / ${HEAT_LOG_CAP}`,
      tone: heatLog.count() > 0 ? 'data' : 'fg-mute',
    },
  ];

  const onRequestPersistent = async () => {
    await storage.requestPersistent();
  };

  // ── Section data ──────────────────────────────────────────────
  const deviceItems = (): DiagDef[] => [
    { l: 'USER AGENT', v: truncateMiddle(navigator.userAgent, 88) },
    { l: 'PLATFORM', v: navigator.platform || '—' },
    { l: 'HW THREADS', v: String(navigator.hardwareConcurrency ?? '—'), tone: 'data' },
    { l: 'DPR', v: String(window.devicePixelRatio || 1), tone: 'data' },
    {
      l: 'VIEWPORT',
      v: `${window.innerWidth}×${window.innerHeight}`,
      tone: 'data',
    },
    { l: 'ORIENTATION', v: screen.orientation?.type ?? '—' },
    { l: 'STANDALONE', v: isStandalone() ? 'YES' : 'NO', tone: isStandalone() ? 'action' : 'warn' },
    {
      l: 'DISPLAY MODE',
      v: matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
    },
  ];

  const iosItems = (): DiagDef[] => {
    if (!isIos()) return [{ l: 'IOS', v: 'n/a — non-iOS device', tone: 'fg-mute' }];
    const wakeOk = meetsWakeLockMinIos();
    return [
      { l: 'IOS VERSION', v: iosVersion?.raw ?? 'unknown', tone: 'data' },
      {
        l: 'INSTALLED PWA',
        v: isStandalone() ? 'YES' : 'NO',
        tone: isStandalone() ? 'action' : 'warn',
      },
      {
        l: 'WAKE LOCK API',
        v: 'wakeLock' in navigator ? 'present' : 'absent',
        tone: 'wakeLock' in navigator ? 'action' : 'hostile',
      },
      {
        l: 'MEETS ≥18.4',
        v: wakeOk ? 'YES' : 'NO',
        tone: wakeOk ? 'action' : 'hostile',
      },
    ];
  };

  const geoItems = (): DiagDef[] => {
    const fix = geo.latest();
    const ago = timeSinceLastFix();
    return [
      {
        l: 'PERMISSION',
        v: geo.permission().toUpperCase(),
        tone:
          geo.permission() === 'granted'
            ? 'action'
            : geo.permission() === 'denied'
              ? 'hostile'
              : 'data',
      },
      { l: 'STATE', v: geo.state().toUpperCase(), tone: trackingBadgeTone(geo.state()) },
      { l: 'FIX COUNT', v: String(geo.fixCount()), tone: 'data' },
      { l: 'TIME SINCE FIX', v: ago === null ? '—' : `${(ago / 1000).toFixed(1)}s`, tone: 'data' },
      { l: 'LAT', v: fix ? fix.lat.toFixed(6) : '—' },
      { l: 'LNG', v: fix ? fix.lng.toFixed(6) : '—' },
      {
        l: 'ACCURACY',
        v: fix ? `${fix.accuracy.toFixed(1)}m` : '—',
        tone: fix && fix.accuracy < 30 ? 'action' : 'warn',
      },
      { l: 'SPEED', v: fix?.speed != null ? `${fix.speed.toFixed(2)} m/s` : '—' },
      { l: 'HEADING', v: fix?.heading != null ? `${fix.heading.toFixed(0)}°` : '—' },
      {
        l: 'SIMULATED',
        v: fix?.simulated ? 'SIM' : 'REAL',
        tone: fix?.simulated ? 'warn' : 'data',
      },
      { l: 'TRACKING', v: formatDuration(trackingDuration()), tone: 'data' },
      {
        l: 'LAST ERROR',
        v: truncateMiddle(geo.lastError() ?? '—', 28),
        tone: geo.lastError() ? 'hostile' : 'fg-mute',
      },
    ];
  };

  const wakeItems = (): DiagDef[] => [
    {
      l: 'SUPPORT',
      v: 'wakeLock' in navigator ? 'YES' : 'NO',
      tone: 'wakeLock' in navigator ? 'action' : 'hostile',
    },
    {
      l: 'STATUS',
      v: wakeLock.status().toUpperCase(),
      tone: wakeLockBadgeTone(wakeLock.status()),
    },
    {
      l: 'DESIRED',
      v: wakeLock.desired() ? 'YES' : 'NO',
      tone: wakeLock.desired() ? 'action' : 'fg-mute',
    },
    {
      l: 'OWNERS',
      v: wakeLock.owners().length > 0 ? wakeLock.owners().join(',').toUpperCase() : '—',
      tone: wakeLock.owners().length > 0 ? 'data' : 'fg-mute',
    },
    { l: 'HELD MS', v: formatDuration(heldDuration()), tone: 'data' },
    { l: 'RE-ACQUIRES', v: String(wakeLock.reacquireAttempts()), tone: 'data' },
    {
      l: 'LAST ERROR',
      v: truncateMiddle(wakeLock.lastError() ?? '—', 28),
      tone: wakeLock.lastError() ? 'hostile' : 'fg-mute',
    },
  ];

  const swItems = (): DiagDef[] => [
    {
      l: 'REGISTERED',
      v: sw.registered() ? 'YES' : 'NO',
      tone: sw.registered() ? 'action' : 'warn',
    },
    { l: 'STATE', v: sw.state().toUpperCase() },
    { l: 'SCOPE', v: truncateMiddle(sw.scope() ?? '—', 28) },
    { l: 'SCRIPT URL', v: truncateMiddle(sw.scriptURL() ?? '—', 28) },
  ];

  const storageItems = (): DiagDef[] => [
    { l: 'QUOTA', v: formatMb(storage.quotaBytes()), tone: 'data' },
    { l: 'USAGE', v: formatMb(storage.usageBytes()), tone: 'data' },
    {
      l: 'USAGE %',
      v:
        storage.quotaBytes() && storage.usageBytes()
          ? `${((storage.usageBytes()! / storage.quotaBytes()!) * 100).toFixed(2)}%`
          : '—',
    },
    {
      l: 'PERSISTED',
      v: storage.persisted() === null ? '—' : storage.persisted() ? 'YES' : 'NO',
      tone: storage.persisted() ? 'action' : 'warn',
    },
    {
      l: 'INDEXEDDB',
      v: typeof indexedDB !== 'undefined' ? 'present' : 'absent',
      tone: typeof indexedDB !== 'undefined' ? 'action' : 'hostile',
    },
    {
      l: 'CACHE API',
      v: typeof caches !== 'undefined' ? 'present' : 'absent',
      tone: typeof caches !== 'undefined' ? 'action' : 'hostile',
    },
  ];

  const networkItems = (): DiagDef[] => {
    const c = network.connection();
    return [
      {
        l: 'ONLINE',
        v: network.online() ? 'YES' : 'NO',
        tone: network.online() ? 'action' : 'hostile',
      },
      { l: 'EFF TYPE', v: c?.effectiveType ?? '—', tone: 'data' },
      { l: 'DOWNLINK', v: c?.downlink != null ? `${c.downlink} Mbps` : '—' },
      { l: 'RTT', v: c?.rtt != null ? `${c.rtt}ms` : '—' },
      { l: 'TYPE', v: c?.type ?? '—' },
      { l: 'SAVE DATA', v: c?.saveData ? 'YES' : 'NO', tone: c?.saveData ? 'warn' : 'data' },
    ];
  };

  const sessionItems = (): DiagDef[] => {
    const startedBy = geo.startedBy();
    return [
      { l: 'UPTIME', v: formatDuration(uptimeMs()), tone: 'data' },
      {
        l: 'VIS STATE',
        v: visibility.state().toUpperCase(),
        tone: visibility.state() === 'visible' ? 'action' : 'hostile',
      },
      {
        l: 'VIS CHANGES',
        v: String(visibility.changes()),
        tone: visibility.changes() === 0 ? 'action' : 'warn',
      },
      { l: 'VISIBLE MS', v: formatDuration(visibility.visibleMs()), tone: 'action' },
      {
        l: 'HIDDEN MS',
        v: formatDuration(visibility.hiddenMs()),
        tone: visibility.hiddenMs() > 0 ? 'hostile' : 'fg-mute',
      },
      {
        l: 'LAST CHANGE',
        v: visibility.lastChangeAt() ? formatDuration(now() - visibility.lastChangeAt()!) : '—',
      },
      {
        l: 'TRACKING SOURCE',
        v: startedBy ? startedBy.toUpperCase() : '—',
        tone: startedBy ? 'data' : 'fg-mute',
      },
      {
        l: 'EVENT COUNT',
        v: `${eventsAccessor().length} / ${EVENT_BUFFER_CAP}`,
        tone: eventsAccessor().length > 0 ? 'data' : 'fg-mute',
      },
    ];
  };

  return (
    <>
      <div
        style={{
          padding: '8px 12px',
          'border-bottom': '1px solid var(--line-faint)',
          display: 'flex',
          'align-items': 'center',
          gap: '8px',
        }}
      >
        <Glyph name="cpu" size={16} color="var(--data)" />
        <span class="t-mono" style={{ 'font-size': '14px', color: 'var(--fg)' }}>
          DIAGNOSTICS
        </span>
        <span class="t-micro">/ platform constraints</span>
        <span style={{ flex: 1 }} />
        <Show when={isDev}>
          <Badge tone="warn">DEV</Badge>
        </Show>
        <Badge tone={geo.state() === 'tracking' ? 'action' : 'data'}>
          {geo.state() === 'tracking' ? '● LIVE' : '○ IDLE'}
        </Badge>
      </div>

      {/* Action surface — pinned so it stays visible during a long session */}
      <div
        style={{
          padding: '10px 12px',
          'border-bottom': '1px solid var(--line)',
          display: 'flex',
          'flex-direction': 'column',
          gap: '8px',
          background: 'var(--sf-panel)',
        }}
      >
        <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap' }}>
          <Btn
            variant="primary"
            onClick={() => void startEverything()}
            disabled={geo.state() === 'tracking'}
            style={{ flex: 1, 'justify-content': 'center', 'min-width': '120px' }}
          >
            <Glyph name="signal" size={12} /> START TRACKING
          </Btn>
          <Btn
            variant="hostile"
            onClick={stopEverything}
            disabled={geo.state() === 'idle' && wakeLock.status() !== 'held'}
            style={{ flex: 1, 'justify-content': 'center', 'min-width': '90px' }}
          >
            <Glyph name="x" size={12} /> STOP
          </Btn>
        </div>
        <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap' }}>
          <Btn
            onClick={onDownloadFixes}
            disabled={geo.fixCount() === 0}
            style={{ flex: 1, 'justify-content': 'center' }}
          >
            <Glyph name="arrow" size={12} /> FIXES ({geo.fixCount()})
          </Btn>
          <Btn
            onClick={onDownloadEvents}
            disabled={eventsAccessor().length === 0}
            style={{ flex: 1, 'justify-content': 'center' }}
          >
            <Glyph name="arrow" size={12} /> EVENTS ({eventsAccessor().length})
          </Btn>
          <Btn
            onClick={() => {
              geo.clearLog();
              clearEvents();
            }}
            disabled={geo.fixCount() === 0 && eventsAccessor().length === 0}
            style={{ 'justify-content': 'center' }}
          >
            CLEAR
          </Btn>
        </div>
        <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap' }}>
          <Btn
            onClick={() => void wakeLock.acquire('diag')}
            disabled={wakeLock.status() === 'held' || wakeLock.status() === 'unsupported'}
            style={{ flex: 1, 'justify-content': 'center' }}
          >
            ACQUIRE WAKELOCK
          </Btn>
          <Btn
            onClick={() => void wakeLock.release('diag')}
            disabled={!wakeLock.desired() && wakeLock.status() !== 'held'}
            style={{ flex: 1, 'justify-content': 'center' }}
          >
            RELEASE
          </Btn>
        </div>
        <Btn
          onClick={() => void onRequestPersistent()}
          disabled={storage.persisted() === true || !storage.supported()}
          style={{ 'justify-content': 'center' }}
        >
          REQUEST PERSISTENT STORAGE
        </Btn>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }} class="no-scroll">
        <DiagSection title="SESSION" sub="critical: visibility / uptime">
          <DiagGrid items={sessionItems()} />
        </DiagSection>

        <DiagSection title="HEAT" sub="gain + decay log · tuning">
          <DiagGrid items={heatItems()} />
          <div style={{ display: 'flex', gap: '6px', 'margin-top': '8px' }}>
            <Btn
              onClick={onDownloadHeat}
              disabled={heatLog.count() === 0}
              style={{ flex: 1, 'justify-content': 'center' }}
            >
              <Glyph name="arrow" size={12} /> HEAT CSV ({heatLog.count()})
            </Btn>
            <Btn
              onClick={() => heatLog.clear()}
              disabled={heatLog.count() === 0}
              style={{ 'justify-content': 'center' }}
            >
              CLEAR
            </Btn>
          </div>
        </DiagSection>

        <DiagSection title="ECONOMY" sub="earn + spend + dual-state log · tuning">
          <DiagGrid items={econItems()} />
          <div
            style={{
              display: 'flex',
              gap: '4px',
              'margin-top': '4px',
              'font-family': 'var(--mono)',
              'font-size': '10px',
            }}
          >
            <For each={tierRows()}>
              {(r) => (
                <div
                  style={{
                    flex: 1,
                    background: 'var(--sf-input)',
                    border: '1px solid var(--line)',
                    padding: '5px 6px',
                  }}
                >
                  <div class="t-micro" style={{ 'margin-bottom': '1px' }}>
                    T{r.t} · n{r.count}
                  </div>
                  <div
                    style={{
                      'font-size': '10.5px',
                      color: r.count > 0 ? 'var(--action)' : 'var(--fg-mute)',
                    }}
                  >
                    {r.count > 0 ? `¤${r.median.toLocaleString()}` : '—'}
                  </div>
                </div>
              )}
            </For>
          </div>
          <Show when={activeDuals().length > 0}>
            <div style={{ 'margin-top': '6px', display: 'flex', 'flex-direction': 'column', gap: '3px' }}>
              <div class="t-micro">ACTIVE DUAL STATES</div>
              <For each={activeDuals()}>
                {(d) => (
                  <div
                    class="t-mono"
                    style={{
                      'font-size': '10px',
                      display: 'flex',
                      gap: '8px',
                      color: 'var(--fg-dim)',
                    }}
                  >
                    <span style={{ flex: 1, 'word-break': 'break-all' }}>{d.id}</span>
                    <Show when={d.mult > 1.001}>
                      <span style={{ color: 'var(--warn)' }}>×{d.mult.toFixed(2)}</span>
                    </Show>
                    <Show when={d.lockSecs > 0}>
                      <span style={{ color: 'var(--hostile)' }}>LOCK {d.lockSecs}s</span>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </Show>
          <div style={{ display: 'flex', gap: '6px', 'margin-top': '8px' }}>
            <Btn
              onClick={onDownloadEcon}
              disabled={econLog.count() === 0}
              style={{ flex: 1, 'justify-content': 'center' }}
            >
              <Glyph name="arrow" size={12} /> ECON CSV ({econLog.count()})
            </Btn>
            <Btn
              onClick={() => econLog.clear()}
              disabled={econLog.count() === 0}
              style={{ 'justify-content': 'center' }}
            >
              CLEAR
            </Btn>
          </div>
        </DiagSection>

        <DiagSection
          title="GEOLOCATION"
          sub={`buffer ${geo.fixes().length} / ${FIX_BUFFER_CAP} · settle ≤${ACCURACY_SETTLED_THRESHOLD_M}m`}
        >
          <DiagGrid items={geoItems()} />
        </DiagSection>

        <DiagSection title="WAKE LOCK">
          <DiagGrid items={wakeItems()} />
        </DiagSection>

        <DiagSection title="DEVICE & BROWSER">
          <DiagGrid items={deviceItems()} />
        </DiagSection>

        <DiagSection title="IOS SPECIFICS">
          <DiagGrid items={iosItems()} />
        </DiagSection>

        <DiagSection title="SERVICE WORKER">
          <DiagGrid items={swItems()} />
        </DiagSection>

        <DiagSection title="STORAGE">
          <DiagGrid items={storageItems()} />
        </DiagSection>

        <DiagSection title="NETWORK">
          <DiagGrid items={networkItems()} />
        </DiagSection>

        <Show when={ghost}>
          <DiagSection title="GHOST WALK" sub="DEV ONLY · synthetic positions">
            <div
              style={{
                display: 'flex',
                'flex-direction': 'column',
                gap: '8px',
                'font-family': 'var(--mono)',
                'font-size': '10.5px',
              }}
            >
              <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
                <span class="t-micro">STATUS</span>
                <Badge tone={ghost!.active() ? 'warn' : 'data'}>
                  {ghost!.active() ? 'SIM ACTIVE' : 'IDLE'}
                </Badge>
                <span style={{ flex: 1 }} />
                <span class="t-micro">
                  POS{' '}
                  <span style={{ color: 'var(--data)' }}>
                    {ghost!.position()[0].toFixed(5)}, {ghost!.position()[1].toFixed(5)}
                  </span>
                </span>
              </div>
              <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
                <span class="t-micro" style={{ width: '40px' }}>
                  SPEED
                </span>
                <input
                  type="range"
                  min="0"
                  max="12"
                  step="0.1"
                  value={ghost!.speedMps()}
                  onInput={(e) => ghost!.setSpeedMps(Number(e.currentTarget.value))}
                  style={{ flex: 1 }}
                />
                <span
                  class="t-mono t-tab"
                  style={{
                    'font-size': '11px',
                    color: 'var(--data)',
                    width: '76px',
                    'text-align': 'right',
                  }}
                >
                  {ghost!.speedMps().toFixed(1)} m/s
                </span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Btn
                  variant="primary"
                  onClick={() => ghost!.start()}
                  disabled={ghost!.active()}
                  style={{ flex: 1, 'justify-content': 'center' }}
                >
                  START SIM
                </Btn>
                <Btn
                  variant="hostile"
                  onClick={() => ghost!.stop()}
                  disabled={!ghost!.active()}
                  style={{ flex: 1, 'justify-content': 'center' }}
                >
                  STOP SIM
                </Btn>
              </div>
            </div>
          </DiagSection>
        </Show>

        <div style={{ padding: '12px', color: 'var(--fg-dim)' }}>
          <div class="t-micro">
            page_load · {new Date(pageLoad).toISOString().replace('T', ' ').slice(0, 19)} UTC
          </div>
        </div>
      </div>
    </>
  );
}
